/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable } from "mobx"

import type { PageApplicationItem } from "../application/application-types"
import { isRemoved } from "./application-apk"
import type {
    ApplicationApk,
    ApplicationDetail,
    ApplicationPageEvent,
    ApplicationPageFlag
} from "./application-detail-types"

export class ApplicationDetailStore {
    /**
     * Detail data can be prefetched from the list response because it has the
     * same fields as `ApplicationDetail` except for `flags`. `setPrefetchedDetail`
     * fills that field with an empty array so the list data can render immediately.
     *
     * The detail request replaces the prefetched value with the complete response,
     * including the device's current flags.
     */
    detail: ApplicationDetail | null = null
    flags: Map<number, ApplicationPageFlag> = new Map()
    events: Map<number, ApplicationPageEvent> = new Map()
    /**
     * True once the last page has been loaded; used to stop pagination.
     */
    flagsLoaded: boolean = false
    /**
     * True once the last page has been loaded; used to stop pagination.
     */
    eventsLoaded: boolean = false
    /**
     * Active and removed APKs from the API, or `null` until loaded.
     *
     * The derived lists split these entries and are cached by MobX until this list
     * changes.
     */
    apks: ApplicationApk[] | null = null

    constructor() {
        makeAutoObservable(this, {
            setPrefetchedDetail: true,
            setDetail: true,
            setFlags: true,
            setEvents: true,
            setApks: true
        })
    }

    get currentFlagsPageIndex(): number {
        return Math.max(...Array.from(this.flags.keys()), 0)
    }

    get currentEventsPageIndex(): number {
        return Math.max(...Array.from(this.events.keys()), 0)
    }

    /**
     * APKs still on the device, or an empty list until loaded.
     */
    get activeApks(): ApplicationApk[] {
        return this.apks?.filter((apk) => !isRemoved(apk)) ?? []
    }

    /**
     * APKs removed from the device, or an empty list until loaded.
     */
    get removedApks(): ApplicationApk[] {
        return this.apks?.filter(isRemoved) ?? []
    }

    setPrefetchedDetail(pageAppDetail: PageApplicationItem): void {
        // Add the detail-only field so the list response satisfies the detail shape.
        this.detail = {
            ...pageAppDetail,
            flags: []
        }
    }

    setDetail(detail: ApplicationDetail): void {
        this.detail = detail
    }

    setFlags(pageNum: number, flags: ApplicationPageFlag): void {
        this.flags.set(pageNum, flags)
        if (flags.last) {
            this.flagsLoaded = true
        }
    }

    setEvents(pageNum: number, events: ApplicationPageEvent): void {
        this.events.set(pageNum, events)
        if (events.last) {
            this.eventsLoaded = true
        }
    }

    setApks(apks: ApplicationApk[]): void {
        this.apks = apks
    }
}
